/* eslint-disable multiline-ternary */
import { LOGGER_LOG_TYPE } from 'Config';
import CompaniesService from 'api/companies/CompaniesService';
import DeviceBrandsService from 'api/deviceBrands/DeviceBrandsService';
import DeviceModelsService from 'api/deviceModels/DeviceModelsService';
import BulkImportService from 'api/bulkImport/BulkImportService';
import { BulkImportResultDto, BulkImportPreviewDto } from 'api/bulkImport/models/BulkImportResultDto';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faDownload, faFileImport, faTimes, faTrash, faSync, faWarning } from '@fortawesome/free-solid-svg-icons';
import styles from './BulkImportScreen.module.scss';

interface ImportRow {
    licensePlate: string;
    deviceCode: string;
    simCardCode: string;
    designation: string;
    deviceBrandId: string;
    deviceModelId: string;
    hasIdentificationTag: boolean;
    deviceModels: SelectValueLabel[];
    fileBrandName?: string;
    fileModelName?: string;
    result?: BulkImportResultDto;
}

const BulkImportVehiclesTab: React.FC = () => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [companyId, setCompanyId] = useState<string>('');
    const [companies, setCompanies] = useState<SelectValueLabel[]>([]);
    const [brands, setBrands] = useState<SelectValueLabel[]>([]);
    const [rows, setRows] = useState<ImportRow[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const [bulkBrandId, setBulkBrandId] = useState<string>('');
    const [bulkModelId, setBulkModelId] = useState<string>('');
    const [bulkModels, setBulkModels] = useState<SelectValueLabel[]>([]);
    const [bulkDesignation, setBulkDesignation] = useState<string>('');
    const [bulkHasIdentificationTag, setBulkHasIdentificationTag] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [preview, setPreview] = useState<BulkImportPreviewDto | null>(null);
    const pendingSyncRef = useRef(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [companiesData, brandsData] = await Promise.all([
                    CompaniesService.getCompanies(),
                    DeviceBrandsService.getDeviceBrands()
                ]);
                setCompanies(companiesData);
                setBrands(brandsData);
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to load lookup data', error);
                toast.error(t('messages.error_load_info'));
            }
        };
        loadData();
    }, []);

    const handleExport = async () => {
        if (!companyId) {
            toast.error(t('bulkImport.select_company'));
            return;
        }
        try {
            Loading.show();
            await BulkImportService.exportCsv(companyId);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to export vehicles', error);
            toast.error(t('bulkImport.export_error'));
        } finally {
            Loading.hide();
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const lines = text.split('\n').filter(line => line.trim().length > 0);
            const parsed: ImportRow[] = [];

            const parseCsvLine = (line: string): string[] => {
                const result: string[] = [];
                let current = '';
                let inQuotes = false;
                for (let i = 0; i < line.length; i++) {
                    const ch = line[i];
                    if (inQuotes) {
                        if (ch === '"') inQuotes = false;
                        else current += ch;
                    } else if (ch === '"') {
                        inQuotes = true;
                    } else if (ch === ',') {
                        result.push(current.trim());
                        current = '';
                    } else {
                        current += ch;
                    }
                }
                result.push(current.trim());
                return result;
            };

            for (const line of lines) {
                const isPipeSeparated = line.includes('--------');
                const parts = isPipeSeparated ? line.split('--------').map(s => s.trim()) : parseCsvLine(line);
                if (parts.length >= 4) {
                    parsed.push({
                        licensePlate: parts[0],
                        deviceCode: parts[1],
                        simCardCode: parts[2],
                        designation: parts[6] || t('bulkImport.without_information') || 'Without information',
                        deviceBrandId: '',
                        deviceModelId: '',
                        hasIdentificationTag: ['true', '1', 'yes', 'sim', 'oui'].includes((parts[5] || '').toLowerCase()),
                        deviceModels: [],
                        fileBrandName: parts[3] || '',
                        fileModelName: parts[4] || ''
                    });
                }
            }

            if (parsed.length === 0) {
                toast.error(t('bulkImport.invalid_format'));
                return;
            }

            const resolveRows = async () => {
                const resolved = [...parsed];
                const modelsCache: Record<string, SelectValueLabel[]> = {};
                for (let i = 0; i < resolved.length; i++) {
                    const row = resolved[i];
                    if (row.fileBrandName) {
                        const matchedBrand = brands.find(b =>
                            b.label?.toLowerCase() === row.fileBrandName?.toLowerCase()
                        );
                        if (matchedBrand?.value) {
                            row.deviceBrandId = matchedBrand.value;
                            try {
                                if (!modelsCache[matchedBrand.value]) {
                                    modelsCache[matchedBrand.value] = await DeviceModelsService.getDeviceModels(matchedBrand.value);
                                }
                                const models = modelsCache[matchedBrand.value];
                                row.deviceModels = models;
                                if (row.fileModelName) {
                                    const matchedModel = models.find(m =>
                                        m.label?.toLowerCase() === row.fileModelName?.toLowerCase()
                                    );
                                    if (matchedModel?.value) {
                                        row.deviceModelId = matchedModel.value;
                                    }
                                }
                            } catch (error) {
                                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to load device models', error);
                            }
                        }
                    }
                }
                setRows(resolved);
            };
            resolveRows();
            setSubmitted(false);
        };
        reader.readAsText(file);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const updateRow = (index: number, field: keyof ImportRow, value: string) => {
        setRows(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleBrandChange = async (index: number, brandId: string) => {
        const updated = [...rows];
        updated[index] = { ...updated[index], deviceBrandId: brandId, deviceModelId: '', deviceModels: [] };
        setRows(updated);

        if (brandId) {
            try {
                const models = await DeviceModelsService.getDeviceModels(brandId);
                setRows(prev => {
                    const newRows = [...prev];
                    newRows[index] = { ...newRows[index], deviceModels: models };
                    return newRows;
                });
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to load device models', error);
            }
        }
    };

    const removeRow = (index: number) => {
        setRows(prev => prev.filter((_, i) => i !== index));
    };

    const handleBulkBrandChange = async (brandId: string) => {
        setBulkBrandId(brandId);
        setBulkModelId('');
        setBulkModels([]);

        let models: SelectValueLabel[] = [];
        if (brandId) {
            try {
                models = await DeviceModelsService.getDeviceModels(brandId);
                setBulkModels(models);
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to load device models', error);
            }
        }

        setRows(prev => prev.map(row =>
            row.result?.success
                ? row
                : { ...row, deviceBrandId: brandId, deviceModelId: '', deviceModels: models }
        ));
    };

    const handleBulkModelChange = (modelId: string) => {
        setBulkModelId(modelId);
        setRows(prev => prev.map(row =>
            row.result?.success
                ? row
                : { ...row, deviceModelId: modelId }
        ));
    };

    const handleBulkDesignationChange = (value: string) => {
        setBulkDesignation(value);
        setRows(prev => prev.map(row =>
            row.result?.success
                ? row
                : { ...row, designation: value }
        ));
    };

    const handleBulkHasIdentificationTagChange = (checked: boolean) => {
        setBulkHasIdentificationTag(checked);
        setRows(prev => prev.map(row =>
            row.result?.success
                ? row
                : { ...row, hasIdentificationTag: checked }
        ));
    };

    const getValidationErrors = (row: ImportRow): string[] => {
        const errors: string[] = [];
        if (!row.licensePlate.trim()) errors.push(t('bulkImport.license_plate'));
        return errors;
    };

    const hasSimCardConflicts = (): boolean => {
        const simCardToDevice = new Map<string, string>();
        for (const row of rows) {
            const sc = row.simCardCode.trim();
            const dc = row.deviceCode.trim();
            if (!sc) continue;
            if (simCardToDevice.has(sc)) {
                if (simCardToDevice.get(sc) !== dc) return true;
            } else {
                simCardToDevice.set(sc, dc);
            }
        }
        return false;
    };

    const buildPayload = () => ({
        companyId: companyId,
        items: rows.map(row => ({
            licensePlate: row.licensePlate.trim(),
            deviceCode: row.deviceCode.trim(),
            simCardCode: row.simCardCode.trim(),
            designation: row.designation.trim() || (t('bulkImport.without_information') ?? 'Without information'),
            companyId: companyId,
            deviceBrandId: row.deviceBrandId || undefined,
            deviceModelId: row.deviceModelId || undefined,
            hasIdentificationTag: row.hasIdentificationTag
        }))
    });

    const validateRows = (): boolean => {
        if (!companyId) {
            toast.error(t('bulkImport.select_company'));
            return false;
        }
        if (rows.length === 0) {
            toast.error(t('bulkImport.no_data'));
            return false;
        }
        if (hasSimCardConflicts()) {
            toast.error(t('bulkImport.sim_card_conflict'));
            return false;
        }
        for (const row of rows) {
            if (getValidationErrors(row).length > 0) {
                toast.error(t('bulkImport.validation_error'));
                return false;
            }
        }
        return true;
    };

    const handleSync = async () => {
        if (!validateRows()) return;
        try {
            Loading.show();
            const previewResult = await BulkImportService.preview(buildPayload());
            setPreview(previewResult);
            setShowConfirmModal(true);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Bulk import preview failed', error);
            toast.error(t('bulkImport.error_message'));
        } finally {
            Loading.hide();
        }
    };

    const handleConfirmSync = () => {
        pendingSyncRef.current = true;
        setShowConfirmModal(false);
    };

    const handleModalExited = async () => {
        if (!pendingSyncRef.current) return;
        pendingSyncRef.current = false;
        try {
            Loading.show();
            const results = await BulkImportService.create(buildPayload());
            setRows(prev => prev.map((row, i) => ({ ...row, result: results.find(r => r.index === i) })));
            setSubmitted(true);
            const createdCount = results.filter(r => r.success && r.action === 'created').length;
            const updatedCount = results.filter(r => r.success && r.action === 'updated').length;
            const errorCount = results.filter(r => !r.success).length;
            const deletedCount = preview?.toDelete?.length ?? 0;
            if (errorCount === 0) {
                toast.success(t('bulkImport.sync_success', { created: createdCount, updated: updatedCount, deleted: deletedCount }));
            } else {
                toast(t('bulkImport.sync_partial', { created: createdCount, updated: updatedCount, deleted: deletedCount, errors: errorCount }));
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Bulk import sync failed', error);
            toast.error(t('bulkImport.error_message'));
        } finally {
            Loading.hide();
        }
    };

    const translateErrors = (error?: string): string => {
        if (!error) return '';
        return error.split(';').map(code => t(`bulkImport.errors.${code.trim()}`) || code).join('; ');
    };

    const isRowInvalid = (row: ImportRow): boolean => getValidationErrors(row).length > 0;

    const hasSimCardDeviceConflict = (row: ImportRow, index: number): boolean => {
        const sc = row.simCardCode.trim();
        if (!sc) return false;
        return rows.some((r, i) => i !== index && r.simCardCode.trim() === sc && r.deviceCode.trim() !== row.deviceCode.trim());
    };

    const getRowClass = (row: ImportRow, index: number): string => {
        if (row.result?.success) return styles.rowSuccess;
        if (row.result && !row.result.success) return styles.rowError;
        if (isRowInvalid(row) || hasSimCardDeviceConflict(row, index)) return styles.rowError;
        return '';
    };

    const pendingRows = rows.filter(r => !r.result?.success);

    return (
        <>
            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
                <Row className="mb-3">
                    <Col xs={12} md={6}>
                        <Form.Group controlId="vehicleCompanyId">
                            <Form.Label>{t('bulkImport.select_company')}*</Form.Label>
                            <Form.Select required value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                                <option value="">{t('common.select_option')}</option>
                                {companies.map(c => (<option key={c.value} value={c.value}>{c.label}</option>))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={6} className="d-flex align-items-end">
                        <Button variant="outline-secondary" onClick={handleExport} disabled={!companyId}>
                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                            {t('bulkImport.export_existing')}
                        </Button>
                    </Col>
                </Row>

                <div className={styles.uploadArea}>
                    <Form.Group controlId="vehicleFileUpload">
                        <Form.Label>{t('bulkImport.upload_file')}</Form.Label>
                        <Form.Control ref={fileInputRef} type="file" accept=".txt,.csv" onChange={handleFileUpload} />
                    </Form.Group>
                </div>

                <div className="alert alert-warning d-flex align-items-center mt-3 mb-2" role="alert">
                    <FontAwesomeIcon icon={faWarning} className="me-2" />
                    {t('bulkImport.sync_warning')}
                </div>

                <div className={styles.formatExample}>
                    <Form.Text muted>
                        <strong>{t('bulkImport.format_example')}:</strong><br />
                        <code>{t('bulkImport.format_header')}</code><br />
                        <strong>{t('bulkImport.format_or_csv')}:</strong><br />
                        <code>{t('bulkImport.format_header_csv')}</code>
                    </Form.Text>
                </div>

                {rows.length > 0 && (
                    <>
                        <div className={styles.tableContainer}>
                            <Table bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>#</th>
                                        <th>{t('bulkImport.license_plate')}</th>
                                        <th>{t('bulkImport.device_code')}</th>
                                        <th>{t('bulkImport.sim_card')}</th>
                                        <th>{t('bulkImport.device_brand')}</th>
                                        <th>{t('bulkImport.device_model')}</th>
                                        <th>{t('bulkImport.designation')}</th>
                                        <th>{t('bulkImport.identification_tag')}</th>
                                        <th style={{ width: '90px' }}>{t('common.actions')}</th>
                                    </tr>
                                    <tr className={styles.bulkFillRow}>
                                        <td></td><td></td><td></td><td></td>
                                        <td>
                                            <Form.Select className={styles.inlineSelect} size="sm" value={bulkBrandId} onChange={(e) => handleBulkBrandChange(e.target.value)}>
                                                <option value="">{t('bulkImport.fill_all')}</option>
                                                {brands.map(b => (<option key={b.value} value={b.value}>{b.label}</option>))}
                                            </Form.Select>
                                        </td>
                                        <td>
                                            <Form.Select className={styles.inlineSelect} size="sm" value={bulkModelId} onChange={(e) => handleBulkModelChange(e.target.value)} disabled={!bulkBrandId}>
                                                <option value="">{t('bulkImport.fill_all')}</option>
                                                {bulkModels.map(m => (<option key={m.value} value={m.value}>{m.label}</option>))}
                                            </Form.Select>
                                        </td>
                                        <td>
                                            <Form.Control className={styles.inlineInput} size="sm" type="text" value={bulkDesignation} onChange={(e) => handleBulkDesignationChange(e.target.value)} placeholder={t('bulkImport.fill_all') ?? ''} />
                                        </td>
                                        <td className="text-center align-middle">
                                            <Form.Check type="checkbox" checked={bulkHasIdentificationTag} onChange={(e) => handleBulkHasIdentificationTagChange(e.target.checked)} />
                                        </td>
                                        <td></td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={index} className={getRowClass(row, index)}>
                                            <td className="align-middle text-center">
                                                {row.result ? (
                                                    row.result.success ? (
                                                        <FontAwesomeIcon icon={faCheck} className={`${styles.statusIcon} ${styles.success}`} />
                                                    ) : (
                                                        <OverlayTrigger placement="right" overlay={<Tooltip>{translateErrors(row.result.error)}</Tooltip>}>
                                                            <span><FontAwesomeIcon icon={faTimes} className={`${styles.statusIcon} ${styles.error}`} /></span>
                                                        </OverlayTrigger>
                                                    )
                                                ) : (index + 1)}
                                            </td>
                                            <td><Form.Control className={styles.inlineInput} size="sm" type="text" value={row.licensePlate} onChange={(e) => updateRow(index, 'licensePlate', e.target.value)} isInvalid={!row.licensePlate.trim()} disabled={row.result?.success} /></td>
                                            <td><Form.Control className={styles.inlineInput} size="sm" type="text" value={row.deviceCode} onChange={(e) => updateRow(index, 'deviceCode', e.target.value)} disabled={row.result?.success} /></td>
                                            <td><Form.Control className={styles.inlineInput} size="sm" type="text" value={row.simCardCode} onChange={(e) => updateRow(index, 'simCardCode', e.target.value)} isInvalid={hasSimCardDeviceConflict(row, index)} disabled={row.result?.success} /></td>
                                            <td>
                                                <Form.Select className={styles.inlineSelect} size="sm" value={row.deviceBrandId} onChange={(e) => handleBrandChange(index, e.target.value)} disabled={row.result?.success}>
                                                    <option value="">{t('common.select_option')}</option>
                                                    {brands.map(b => (<option key={b.value} value={b.value}>{b.label}</option>))}
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Form.Select className={styles.inlineSelect} size="sm" value={row.deviceModelId} onChange={(e) => updateRow(index, 'deviceModelId', e.target.value)} disabled={row.result?.success || !row.deviceBrandId}>
                                                    <option value="">{t('common.select_option')}</option>
                                                    {row.deviceModels.map(m => (<option key={m.value} value={m.value}>{m.label}</option>))}
                                                </Form.Select>
                                            </td>
                                            <td><Form.Control className={styles.inlineInput} size="sm" type="text" value={row.designation} onChange={(e) => updateRow(index, 'designation', e.target.value)} disabled={row.result?.success} /></td>
                                            <td className="text-center align-middle">
                                                <Form.Check type="checkbox" checked={row.hasIdentificationTag} onChange={(e) => { setRows(prev => { const updated = [...prev]; updated[index] = { ...updated[index], hasIdentificationTag: e.target.checked }; return updated; }); }} disabled={row.result?.success} />
                                            </td>
                                            <td className="text-center align-middle">
                                                {!row.result?.success && (
                                                    <Button variant="outline-danger" size="sm" onClick={() => removeRow(index)} title={t('bulkImport.remove_row') ?? ''}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        <div className={styles.buttonsContainer}>
                            <div className={styles.summary}>
                                {submitted ? (
                                    <span>{t('bulkImport.results')}: {rows.filter(r => r.result?.success && r.result?.action === 'created').length} {t('bulkImport.created_label')} / {rows.filter(r => r.result?.success && r.result?.action === 'updated').length} {t('bulkImport.updated_label')} / {rows.filter(r => r.result && !r.result.success).length} {t('bulkImport.error_label')}</span>
                                ) : (
                                    <span>{rows.length} {t('bulkImport.items_count')}</span>
                                )}
                            </div>
                            {pendingRows.length > 0 && (
                                <Button variant="primary" className={styles.button} onClick={handleSync}>
                                    <FontAwesomeIcon icon={submitted ? faFileImport : faSync} className="me-2" />
                                    {submitted ? t('bulkImport.retry_failed') : t('bulkImport.sync')}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} onExited={handleModalExited}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('bulkImport.confirm_sync_title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {preview && (
                        <>
                            <p>{t('bulkImport.confirm_sync_summary', { create: preview.toCreate, update: preview.toUpdate, delete: preview.toDelete.length })}</p>
                            {preview.toDelete.length > 0 && (
                                <>
                                    <p className="text-danger fw-bold">{t('bulkImport.confirm_delete_warning')}</p>
                                    <ul className="mb-0">
                                        {preview.toDelete.map((plate, i) => (<li key={i}>{plate}</li>))}
                                    </ul>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>{t('common.cancel')}</Button>
                    <Button variant="primary" onClick={handleConfirmSync}>{t('bulkImport.confirm_sync')}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BulkImportVehiclesTab;
